<template>
  <div class="agent-communication communication" :class="{'fixed': isFixed }" pa-0>
    <template v-if="isMobile">
      <transition name="slide-left" class="height">
        <v-flex v-if="isMobile && !selectedDialogUser" xs12>
          <v-container class="communication-container">
            <template>
              <dialogs-list
                :dialogs="agentDialogs"
                @select-dialog="selectDialog($event)"
              />
            </template>
          </v-container>
        </v-flex>
      </transition>
      <transition name="slide-left">
        <v-flex
          v-if="selectedDialogUser && !selectedThread && !showNewThreadForm"
          xs12
          class="messages-section"
        >
          <filter-message-container
            :threads="threads"
            :sender="agentData"
            :saved-sort="savedSort"
            :client-name="selectedDialogUser.name"
            @update-saved-sort="saveSort($event)"
            @on-new-thread="removeShowingThread()"
          />
        </v-flex>
      </transition>
      <transition name="slider-right">
        <div v-if="selectedThread">
          <!-- <client-info-header :name="selectedDialogUser.name" /> -->
          <thread-header-manager
            v-if="threadHeaderNeeded"
            :thread="selectedThread.thread"
            @open-home-details="openHomeDetails($event)"
          />
          <div
            class="user-dialog_wrapper"
            :class="{ 'no-reply-message': !showReplyMessage }"
          >
            <messages-list
              :messages="selectedThread.messages"
              :sender="agentData"
              :receiver="selectedDialogUser"
              :client-routes="getClientRoutes"
            />
          </div>
          <reply-message
            v-if="showReplyMessage"
            @on-submit="onSubmitNewMessage($event)"
          />
        </div>
        <div
          v-else-if="showNewThreadForm && selectedDialogUser"
          class="message-section__thread"
        >
          <new-thread
            :receiver="selectedDialogUser"
            @on-message="startNewThread($event)"
          />
        </div>
      </transition>
      <property-view-modal
        :home-info="homeInfo"
        :show-modal="showHomeInfoModal"
        @close-modal="showHomeInfoModal = false"
      />
    </template>
    <div v-else>
      <v-layout id="containerClientCommunication">
        <div class="list-section">
          <div v-if="!isMobile" class="communication-panel-container">
            <dialogs-list
              :active-dialog="
                selectedDialogUser ? selectedDialogUser._id : ''
              "
              :dialogs="agentDialogs"
              @select-dialog="selectDialog($event)"
            />
          </div>
        </div>
        <div v-if="selectedDialogUser" class="messages-section">
          <div class="messages-section__content">
            <div class="filter-message-section">
              <info-header>
                <span>
                  {{ selectedDialogUser.name }}
                </span>
              </info-header>
              <filter-message-container
                :threads="threads"
                :sender="agentData"
                :saved-sort="savedSort"
                @update-saved-sort="saveSort($event)"
                @on-new-thread="removeShowingThread()"
              />
            </div>

            <div class="message-section__thread">
              <info-header>
                <span v-if="!isMobile" class="price">
                  <span v-if="isShowPrice()" class="price-range">
                    Price range
                    {{ currencyFormatter(selectedDialogUser.priceMin) }} -
                    {{ currencyFormatter(selectedDialogUser.priceMax) }}
                  </span>
                  <span v-else>
                    Price not selected
                  </span>
                  <span v-if="selectedDialogUser.goalDate">
                    Goal Date
                    {{ formatGoalDate(selectedDialogUser.goalDate) }}
                  </span>
                  <span v-else>Goal Date not selected</span>
                </span>
                <span v-else class="price">
                  <span v-if="isShowPrice()" class="price-range">
                    {{ currencyFormatter(selectedDialogUser.priceMin) }} -
                    {{ currencyFormatter(selectedDialogUser.priceMax) }}
                  </span>
                  <span v-if="selectedDialogUser.goalDate">
                    Goal Date
                    {{ formatGoalDate(selectedDialogUser.goalDate) }}
                  </span>
                </span>
              </info-header>
              <template v-if="selectedThread">
                <thread-header-manager
                  v-if="threadHeaderNeeded"
                  :thread="selectedThread.thread"
                />
                <div
                  class="user-dialog_wrapper"
                  :class="{ 'no-reply-message': !showReplyMessage }"
                >
                  <messages-list
                    :messages="selectedThread.messages"
                    :sender="agentData"
                    :receiver="selectedDialogUser"
                    :client-routes="getClientRoutes"
                  />
                </div>
                <reply-message
                  v-if="showReplyMessage"
                  @on-submit="onSubmitNewMessage($event)"
                />
              </template>
              <template v-else>
                <new-thread
                  :receiver="selectedDialogUser"
                  @on-message="startNewThread($event)"
                />
              </template>
            </div>
          </div>
        </div>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { get } from 'lodash'
import uuidv4 from 'uuid/v4'
import moment from 'moment'
import currencyFormatter from 'currency-formatter'

import DialogsList from '../../components/communication/DialogsList'
import NewThread from '../../components/communication/NewThread'
import FilterMessageContainer from '../../components/communication/FilterMessageContainer'
import ReplyMessage from '../../components/communication/ReplyMessage'
import MessagesList from '../../components/communication/MessagesList'
import InfoHeader from '../../components/communication/InfoHeader.vue'
import PropertyViewModal from '../../components/modals/PropertyViewModal'
import ThreadHeaderManager from '../../components/communication/thread-headers/ThreadHeaderManager'
import { sendMessage, createNewThread } from '../../services/messagesService'
import { isEmpty } from '../../utils/generalUtils'

export default {
  components: {
    MessagesList,
    NewThread,
    ReplyMessage,
    DialogsList,
    FilterMessageContainer,
    InfoHeader,
    ThreadHeaderManager,
    PropertyViewModal
  },

  data() {
    return {
      selectedMessages: [],
      threadsWithUser: [],
      savedSort: null,
      threadTypes: [
        'home_message',
        'provide_feedback_message',
        'offer_message',
        'common_message',
        'route_schedule_message'
      ],
      showHomeInfoModal: false,
      homeInfo: null
    }
  },

  computed: {
    ...mapState('common', ['isMobile', 'showSingleRoutePropertyView']),
    ...mapState('home', ['windowWidth']),
    ...mapState('agent', [
      'agentData',
      'agentMessages',
      'agentDialogs',
      'selectedDialogUser',
      'agentRouteData'
    ]),
    ...mapGetters('agent', ['agentUnreadMessages']),
    ...mapState('communication', [
      'selectedThread',
      'showNewThreadForm',
      'threads'
    ]),

    isFixed() {
      if (!this.isMobile) return false
      if (this.showSingleRoutePropertyView) return false
      return true
    },

    getClientRoutes() {
      if (this.selectedDialogUser) {
        return this.agentRouteData.filter(
          route => route.clientId === this.selectedDialogUser._id
        )
      }
      return []
    },

    threadHeaderNeeded() {
      if (!this.selectedThread) return false
      return this.threadTypes.includes(this.selectedThread.thread.type)
    },

    showReplyMessage() {
      return get(this.selectedThread, 'thread.canReply', true)
    },

    showMobileLayout() {
      return this.windowWidth < 960
    }
  },

  watch: {
    getDialogsUsers() {
      this.getDialogsUsers()
    }
  },

  mounted() {
    this.getDialogsUsers()
  },

  destroyed() {
    this.setDialogUser(null)
    this.setShowNewThreadForm(false)
    this.setShowingMobileBackButton(false)
  },

  methods: {
    ...mapActions('agent', [
      'markMessagesAsRead',
      'getDialogsUsers',
      'setDialogUser'
    ]),
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    ...mapMutations('communication', {
      selectThread: 'SET_THREAD',
      setThreads: 'SET_THREADS',
      setShowNewThreadForm: 'SET_SHOW_NEW_THREAD_FORM'
    }),
    ...mapMutations('agent', {
      setAgentMessages: 'SET_AGENT_MESSAGES'
    }),

    currencyFormatter(value) {
      return currencyFormatter.format(value, { code: 'USD' }).slice(0, -3)
    },

    saveSort(sort) {
      this.savedSort = sort
    },

    async selectDialog(user) {
      this.setDialogUser(user)
      const foundUser = this.agentDialogs.find(u => u._id === user._id)
      await this.markMessagesAsRead({
        user: foundUser,
        messages: foundUser.unreadMessages
      })
      this.setShowingMobileBackButton(true)
    },

    isShowPrice() {
      if (
        typeof this.selectedDialogUser.priceMin !== 'number' ||
        typeof this.selectedDialogUser.priceMax !== 'number'
      ) {
        return false
      }
      return true
    },

    async onSubmitNewMessage(message) {
      const newMessage = (
        await sendMessage({
          to: [this.selectedDialogUser._id],
          message,
          subject: 'Chat message',
          metadata: {
            data: {
              thread: this.selectedThread.thread
            }
          }
        })
      ).data
      newMessage.from = this.agentData._id
      const th = { ...this.selectedThread }
      th.messages.push(newMessage)
      this.setShowNewThreadForm(false)
      this.selectThread(th)

      // update threads array
      const updatedThreads = this.threads.map(thread => {
        return thread.thread.id === th.thread.id ? th : thread
      })
      this.setThreads(updatedThreads)

      // update agent messages
      this.updateAgentMessages(newMessage)
    },

    removeShowingThread() {
      this.selectThread(null)
      this.setShowNewThreadForm(true)
    },

    formatGoalDate(goalDate) {
      return moment(goalDate).format('MM-DD-YYYY')
    },

    async startNewThread(data) {
      const result = (
        await createNewThread({
          to: [this.selectedDialogUser._id],
          message: data.message,
          subject: data.subject,
          threadId: uuidv4()
        })
      ).data

      result.from = this.agentData._id
      this.setThreads([
        ...this.threads,
        {
          messages: [result],
          thread: result.metadata.data.thread
        }
      ])
      this.setShowNewThreadForm(false)
      this.selectThread(this.threads[this.threads.length - 1])
      this.updateAgentMessages(result)
    },
    updateAgentMessages(message) {
      this.setAgentMessages(
        isEmpty(this.agentMessages)
          ? [message]
          : [...this.agentMessages, message]
      )
    },
    openHomeDetails(home) {
      this.showHomeInfoModal = true
      this.homeInfo = home
    }
  }
}
</script>

<style lang="sass">
.agent-communication .messages-section
  padding-right: 20px
  flex: 1 0
  height: calc(100vh - 31px)
  @media only screen and (max-width: 600px)
    padding-right: 0

.messages-section__content
  display: flex
  height: 100%

.message-section__thread
  width: 100%
  overflow: hidden

.filter-message-section
  flex-wrap: wrap
  max-width: 250px
  min-width: 250px
  display: flex
  height: 100%
  flex-direction: row
  margin-right: 20px

.list-section
  width: 260px
  padding: 0px 20px 0 0px

.height
  height: 100%

.communication
  background: $hwayzBackgroundColor
  display: block
  align-items: stretch
  overflow: scroll
  height: calc(100vh - 48px)
  .route-details-home
    padding: 0 !important
    margin: 0 !important
  .v-datatable
    @media only screen and (max-width: 1024px)
      th, td
        font-size: 12px
        padding: 0 5px !important
      td:first-child
        padding-left: 20px !important

.communication-container
  height: 100%
  overflow-y: scroll

.thread-header + .user-dialog_wrapper
  height: calc(100vh - 258px)

.user-dialog_wrapper.no-reply-message
  height: calc(100vh - 134px)

.user-dialog_wrapper
  background: #fff
  height: calc(100vh - 223px)
  min-height: 0px
  overflow-y: scroll

.communication-panel-container
  background: #fff
  border: 1px solid #ececec
  padding: 0 10px

.slide-right-enter-active, .slide-right-leave-active, .slide-left-enter-active, .slide-left-leave-active,.slide-down-enter-active, .slide-down-leave-active
  transition: all 0.4s

.slide-right-enter, .slide-right-leave-to
  transform: translate(100%, 0)

.slide-left-enter, .slide-left-leave-to
  transform: translate(-100%, 0)

.slide-down-enter, .slide-down-leave-to
  transform: translate(0, 100%)

@media only screen and (max-width: $bp-phone-only)
  .messages-section__title
    text-align: center
    padding-left: 0
    font-size: 16px
    margin: 10px

  .thread-header + .user-dialog_wrapper
    height: calc(100% - 148px)
  .thread-header + .user-dialog_wrapper.no-reply-message
    height: calc(100vh - 119px)
    calc(100vh - 118px)
  .user-dialog_wrapper
    height: calc(100vh - 169px)
  .messages-section
    height: calc(100vh - 56px)

  .communication.fixed
    width: 100%
    height: 100%
    padding-top: 48px
    position: fixed
    overflow: hidden
    top: 0
    > div
      height: 100%

@media only screen and (min-width: 600px) and (max-width: 960px)
  .list-section
    padding: 25px 0 0
    flex: 0 0 140px
    font-size: 12px
  .planner-comm__name
    .v-avatar
      display: none
  .communication-panel-container
    height: 100%
    .search-container
      display: none
  .communication
    .messages-section__title
      margin-top: 25px
    .filter-message-section
      margin-right: 0
    .messages-section__title
      border-radius: 0px
    .message__body
      padding-left: 0
  .dialogs-list
    height: calc(100vh - 90px)
  .thread-header
    span
      display: none
  .filter-message-section
    max-width: 200px
    min-width: 200px
    .date-filter
      display: none
    .thread-con
      padding: 0.5rem 0
      font-size: 12px
    .thread__subtitle
      max-width: 150px

.price-range
  margin-right: 30px
.communication.fixed
    width: 100%
    height: 100%
    padding-top: 48px
    position: fixed
    overflow: hidden
    top: 0
    > div
      height: 100%
</style>
